"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";
import posthog from "posthog-js";
import { isNetworkError, waitForOnline } from "@/lib/api-client";

/** Groups whose users belong to a clinic (vs. e.g. "partner", who has none).
 * Single source for "does this user have clinic-scoped data" checks. */
export const CLINIC_GROUPS = ["admin", "owner", "member"] as const;

/** Whether this user belongs to a clinic. Partner-only users do not, and every
 * clinic-scoped surface (app-pages layout, menu data, /refer) asks here. */
export function hasClinicRole(user: { groups?: string[] } | null | undefined): boolean {
  return user?.groups?.some((g) => (CLINIC_GROUPS as readonly string[]).includes(g)) ?? false;
}

export interface User {
  username: string;
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  signInDetails?: any;
  groups?: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: () => boolean;
  hasGroup: (groupName: string) => boolean;
  signOut: () => Promise<void>;
  checkUser: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkUser = useCallback(async () => {
    const hydrate = async (forceRefresh: boolean) => {
      // These three Cognito calls don't depend on each other's results, so run
      // them concurrently instead of as a 3-round-trip waterfall — this is the
      // main cost of the blocking spinner every (app-pages) screen sits behind
      // on first load.
      const [currentUser, session, attributes] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession({ forceRefresh }),
        fetchUserAttributes(),
      ]);
      const groups = session.tokens?.accessToken?.payload["cognito:groups"] as string[] || [];

      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        email: attributes.email,
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        signInDetails: currentUser.signInDetails,
        groups,
      });
      setIsAuthenticated(true);
    };

    // Retry hydration to tolerate transient Cognito failures (e.g. a GetUser /
    // fetchAuthSession 400 during session hydration). Only declare the user
    // unauthenticated after retries are exhausted: the (app-pages) layout does an
    // unrecoverable window.location redirect to /signin on isAuthenticated=false,
    // so a single transient error must not evict a genuinely signed-in user.
    // Mirrors getCognitoToken()'s retry in api-client.ts.
    const retries = 3;
    const delayMs = 300;
    // Accumulates across all attempts (mirrors getCognitoToken()'s semantics) rather
    // than tracking only the last attempt, so a mixed sequence still waits for
    // connectivity if any attempt failed for network reasons.
    let sawNetworkError = false;

    for (let i = 0; i < retries; i++) {
      try {
        await hydrate(i > 0);
        setLoading(false);
        return;
      } catch (error) {
        if (isNetworkError(error)) {
          sawNetworkError = true;
        }
        if (i < retries - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
    }

    // A dead connection (e.g. laptop waking from sleep before Wi-Fi reassociates) looks
    // identical to being signed out here. Give it one more chance once the browser
    // reports connectivity, instead of evicting a genuinely signed-in user.
    if (sawNetworkError) {
      await waitForOnline(10_000);
      try {
        await hydrate(true);
        setLoading(false);
        return;
      } catch (error) {
        if (isNetworkError(error)) {
          // Still offline after waiting — do NOT declare the user signed out. Leave
          // `loading` true so the (app-pages) layout's redirect-on-false never fires;
          // the next mount/navigation will retry hydration.
          return;
        }
        // Genuine Cognito rejection once connectivity returned — fall through to sign-out.
      }
    }

    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      // Clear PostHog identity (distinct_id + person properties + cached flags)
      // before the redirect, so a different user on the same browser does not
      // inherit the previous person's feature-flag evaluation. Guarded on the
      // SDK's actual init state (same signal PostHogProvider uses) so reset() is
      // never called uninitialized — e.g. local dev without the key, or a failed
      // init — which would log a warning.
      if (posthog.__loaded) {
        posthog.reset();
      }
      window.location.href = "/signin";
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (error) {
      console.error("Error getting access token:", error);
      return null;
    }
  }, []);

  const isAdmin = useCallback(() => {
    return user?.groups?.includes("admin") || false;
  }, [user]);

  const hasGroup = useCallback((groupName: string) => {
    return user?.groups?.includes(groupName) || false;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        isAdmin,
        hasGroup,
        signOut,
        checkUser,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
